import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { login, logout, setUser } from '../../store/authSlice';
import { showToast } from '../../store/uiSlice';
import { invitationsApi, InvitationPreview } from '../../api/endpoints/invitationsApi';
import { UserRole } from '../../types/common';
import { decodeJwtPayload } from '../../utils/jwt';
import Logo from '../../assets/header-logo.svg';

type LoadState = 'loading' | 'ready' | 'error';

const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState('');
  const [invitation, setInvitation] = useState<InvitationPreview | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Existing-user login form
  const [loginUserName, setLoginUserName] = useState('');
  const [password, setPassword] = useState('');

  // New-user signup form
  const [fullName, setFullName] = useState('');
  const [userName, setUserName] = useState('');
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (!token) return;
    invitationsApi
      .getInvitation(token)
      .then((preview) => {
        setInvitation(preview);
        setLoadState('ready');
      })
      .catch((error: any) => {
        setLoadError(
          error?.response?.data?.message ||
            'This invitation link is invalid or has expired.'
        );
        setLoadState('error');
      });
  }, [token]);

  const goToWorkspace = (
    workspaceId: string,
    workspaceName: string,
    boardId?: string | null,
    boardName?: string | null
  ) => {
    dispatch(
      showToast({
        message: boardName
          ? `You've joined ${workspaceName} and "${boardName}"!`
          : `You've joined ${workspaceName}!`,
        type: 'success',
      })
    );
    navigate(boardId ? `/boards/${boardId}` : `/workspace/${workspaceId}`);
  };

  const acceptAsCurrentUser = async () => {
    if (!token) return;
    setSubmitting(true);
    setFormError('');
    try {
      const result = await invitationsApi.acceptInvitation(token, {});
      goToWorkspace(result.workspaceId, result.workspaceName, result.boardId, result.boardName);
    } catch (error: any) {
      setFormError(
        error?.response?.data?.message || error?.message || 'Failed to accept invitation'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoginThenAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !invitation) return;
    setSubmitting(true);
    setFormError('');
    try {
      await dispatch(login({ userName: loginUserName, password })).unwrap();
      const result = await invitationsApi.acceptInvitation(token, {});
      goToWorkspace(result.workspaceId, result.workspaceName, result.boardId, result.boardName);
    } catch (error: any) {
      setFormError(
        error?.response?.data?.message || error?.message || 'Login failed'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignupThenAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setFormError('');
    try {
      const result = await invitationsApi.acceptInvitation(token, {
        fullName,
        userName,
        password: newPassword,
      });

      if (result.token) {
        localStorage.setItem('authToken', result.token);
        const payload = decodeJwtPayload(result.token);
        const id = payload?.userId ?? payload?.sub;
        dispatch(
          setUser({
            id: id !== undefined && id !== null ? String(id) : `user-${Date.now()}`,
            email: result.email,
            name: fullName,
            userName,
            role: UserRole.MEMBER,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          })
        );
      }

      goToWorkspace(result.workspaceId, result.workspaceName, result.boardId, result.boardName);
    } catch (error: any) {
      setFormError(
        error?.response?.data?.message || error?.message || 'Failed to create account'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (loadState === 'loading') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (loadState === 'error' || !invitation) {
      return (
        <>
          <Alert severity="error" sx={{ mb: 3 }}>
            {loadError}
          </Alert>
          <Button component={Link} to="/" fullWidth variant="contained">
            Go to login
          </Button>
        </>
      );
    }

    const emailMismatch =
      isAuthenticated && user?.email && user.email.toLowerCase() !== invitation.email.toLowerCase();

    if (isAuthenticated && !emailMismatch) {
      return (
        <>
          <Typography variant="body1" sx={{ mb: 3 }}>
            You're signed in as <strong>{user?.email}</strong>. Accept this invite to join{' '}
            <strong>{invitation.workspaceName}</strong> as a{' '}
            <strong>{invitation.role}</strong>
            {invitation.boardName ? (
              <>
                {' '}
                (with access to <strong>{invitation.boardName}</strong>)
              </>
            ) : null}
            ?
          </Typography>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={submitting}
            onClick={acceptAsCurrentUser}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : 'Accept invitation'}
          </Button>
        </>
      );
    }

    if (isAuthenticated && emailMismatch) {
      return (
        <>
          <Alert severity="warning" sx={{ mb: 3 }}>
            This invitation was sent to <strong>{invitation.email}</strong>, but you're signed
            in as <strong>{user?.email}</strong>. Log out to continue with the invited account.
          </Alert>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => dispatch(logout())}
          >
            Log out
          </Button>
        </>
      );
    }

    // Not authenticated
    return (
      <>
        <Typography variant="body1" sx={{ mb: 3 }}>
          You've been invited to join <strong>{invitation.workspaceName}</strong> as a{' '}
          <strong>{invitation.role}</strong>
          {invitation.boardName ? (
            <>
              {' '}
              with access to the <strong>{invitation.boardName}</strong> board
            </>
          ) : null}
          .
        </Typography>

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        {invitation.userExists ? (
          <Box component="form" onSubmit={handleLoginThenAccept}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              An account already exists for {invitation.email}. Log in to accept.
            </Typography>
            <TextField
              fullWidth
              label="Username"
              value={loginUserName}
              onChange={(e) => setLoginUserName(e.target.value)}
              required
              autoFocus
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              sx={{ mb: 3 }}
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Log in & accept'}
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSignupThenAccept}>
            <TextField
              fullWidth
              label="Email"
              value={invitation.email}
              disabled
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              autoFocus
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Username"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              inputProps={{ minLength: 6 }}
              helperText="At least 6 characters"
              sx={{ mb: 3 }}
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Create account & join'
              )}
            </Button>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Not you? <Link to="/">Log in with a different account</Link>
        </Typography>
      </>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      }}
    >
      <Container maxWidth="xs">
        <Paper sx={{ p: 4, borderRadius: 3 }} elevation={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box component="img" src={Logo} alt="Agile Edge" sx={{ height: 32 }} />
            <Typography variant="h6" fontWeight={700} sx={{ textTransform: 'lowercase' }}>
              agile edge
            </Typography>
          </Box>
          {renderBody()}
        </Paper>
      </Container>
    </Box>
  );
};

export default AcceptInvite;
